import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  signal,
  WritableSignal
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { ActivatedRoute, Router, ParamMap, Params } from '@angular/router';

import { Subject, Subscription, debounceTime, tap } from 'rxjs';

import { AppConfig } from '../app-config';
import { Dictionary } from '../_types/dictionary';
import {
  getTimeframes,
  Timeframe,
  TimeframeType
} from './timeframes';

@Component({
  selector: 'apusage',
  templateUrl: './usage.component.html',
  styleUrls: ['./usage.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UsageComponent {
  public tableModel: WritableSignal<ReportTableModel>;
  private payload: UsageReportPayload;
  public hasData: boolean = false;
  public selectedTimeframe: Timeframe | null = null;
  public loading: WritableSignal<boolean> = signal<boolean>(true);
  public timeframes: Timeframe[];
  public earliest: Date | null = null;
  public usersFilters: string[] = ['Subscribers', 'Internal Users', 'All Users'];
  public selectedUsersFilter: string = this.usersFilters[0];

  constructor(
    private route: ActivatedRoute,
    private httpClient: HttpClient,
    private applicationConfig: AppConfig, 
  ) {
    this.route.queryParams.subscribe((queryParams) => {
      // Do something with this - I imagine they might like to send URLs of system usage reports
      // around internally, when I have URL filters for each column implemented
    });
    this.payload = {
      usersFilter: this.selectedUsersFilter, dateFrom: null, dateTo: null, utcOffset: new Date().getTimezoneOffset()
    };
    this.tableModel = signal<ReportTableModel>(new ReportTableModel([], 0));
    this.getEarliestTimestamp();
  }

  private loadReport() {
    this.loading.set(true);
    this.httpClient.post<SystemUsageReport>(`${this.applicationConfig.applicationUri}/v2/systemusage/`, this.payload)
      .subscribe(
        {
          next: result => {
            let model: ReportTableModel = new ReportTableModel(result.interfaces, result.total);
            let hasData: boolean = false;
            result.userResults.forEach((userResult: SystemUsageUserResults, index: number) => {
              hasData = true;
              let row = new ReportRow(userResult.user.fullName, userResult.total);
              model.interfaces.forEach((ifd: InterfaceDto) => {
                ifd.modules.forEach((mod: ModuleDto) => {
                  mod.actions.forEach((action: ActionDto) => {
                    row.cells.push(
                      new ReportCell(
                        userResult.data[SystemUsageInterface[ifd.interface]] !== undefined
                          && userResult.data[SystemUsageInterface[ifd.interface]][SystemUsageModule[mod.module]] !== undefined
                          && userResult.data[SystemUsageInterface[ifd.interface]][SystemUsageModule[mod.module]][SystemUsageAction[action.action]] !== undefined
                          ? userResult.data[SystemUsageInterface[ifd.interface]][SystemUsageModule[mod.module]][SystemUsageAction[action.action]]
                          : null));
                  });
                });
              });
              model.rows.push(row);
            });
            this.tableModel.set(model);
            this.hasData = hasData;
            this.loading.set(false);
          }
        }
      );
  }

  public getEarliestTimestamp() {
    this.httpClient.get<EarliestTimestampResponse>(`${this.applicationConfig.applicationUri}/v2/systemusage/earliest`)
      .subscribe(
        {
          next: result => {
            this.earliest = typeof result.earliestTimestamp === 'string'
              ? new Date(result.earliestTimestamp)
              : result.earliestTimestamp;
            this.earliest = new Date(this.earliest.getTime() - (this.earliest.getTimezoneOffset() * 60 * 1000));
            this.timeframes = getTimeframes(this.earliest);
            this.selectedTimeframe = this.timeframes.find(tf => tf.selected);
            this.onTimeframeChange(this.selectedTimeframe);
            this.loadReport();
          }
        }
      );

  }

  public onTimeframeChange(timeframe: Timeframe) {
    this.payload.dateFrom = timeframe ? (timeframe.start ? this.toUTCStartOfDay(timeframe.start) : null) : null;
    this.payload.dateTo = timeframe ? (timeframe.end ? this.toUTCEndOfDay(timeframe.end) : null) : null;
    this.loadReport();
  }

  public onUsersFilterChange(usersFilter: string) {
    this.payload.usersFilter = usersFilter;
    this.loadReport();
  }

  public getInterfaceName(iface: SystemUsageInterface): string {
    return SystemUsageInterface[iface].replace(/_/g, ' ');
  }

  public getModuleName(module: SystemUsageModule): string {
    return SystemUsageModule[module].replace(/_/g, ' ');
  }

  public getActionName(action: SystemUsageAction): string {
    return SystemUsageAction[action].replace(/_/g, ' ');
  }

  private toUTCStartOfDay(date: Date): Date {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
  }

  private toUTCEndOfDay(date: Date): Date {
    // Create a date at 23:59:59.999 UTC for the given local date
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999));
  }
}

class ReportTableModel {
  public interfaces: InterfaceModel[];
  public rows: ReportRow[];
  public total: number;

  constructor(ifaces: InterfaceDto[], total: number) {
    this.interfaces = [];
    ifaces.forEach((iface: InterfaceDto) => {
      this.interfaces.push(new InterfaceModel(iface));
    });
    this.rows = [];
    this.total = total;
  }
}

class ReportRow {
  public user: string;
  public cells: ReportCell[];
  public total: number;

  constructor(user: string, total: number) {
    this.user = user;
    this.total = total;
    this.cells = [];
  }
}

class ReportCell {
  public value: string | number | null;
  constructor(value: string | number | null) {
    this.value = value;
  }
}

enum SystemUsageModule {
  Catalog = 1,
  Landing_Page = 2,
  Library = 3,
  Moducules = 4,
  Package_Export = 6,
  Reports = 7,
  Services_Matrix = 8,
  V1 = 9,
  System_Usage = 10,
}

enum SystemUsageAction {
  Add_Row = 1,
  Approve = 2,
  Browse = 3,
  Check_Version_Compatibility = 4,
  Delete = 5,
  Delete_Column_Value = 6,
  Delete_Rows = 7,
  Download = 8,
  Edit = 9,
  Export = 10,
  Get_Cell_Change_History = 11,
  Get_Room_Detail = 12,
  Landing_Page = 13,
  Load_Data = 14,
  Load_Master = 15,
  Load_Program = 16,
  Load_Project = 17,
  Part_Placed = 18,
  Save = 19,
  Package_Export = 20,
  Report = 21,
  Part_Saved = 22
}

enum SystemUsageInterface {
  Classic_Interface = 1,
  New_Interface = 2,
  SpaceBot = 3
}

interface BasicUserInformation {
  id: string;
  userName: string;
  fullName: string;
}

interface SystemUsageReport {
  interfaces: InterfaceDto[];
  userResults: SystemUsageUserResults[];
  total: number;
}

class SystemUsageUserResults {
  user: BasicUserInformation;
  data: Dictionary<SystemUsageInterface, Dictionary<SystemUsageModule, Dictionary<SystemUsageAction, number>>>;
  total: number;

  getCell(iface: SystemUsageInterface, module: SystemUsageModule, action: SystemUsageAction): number | string {
    if (this.data[iface] === undefined || this.data[iface][module] === undefined || this.data[iface][module][action] === undefined) return '&nbsp;';
    return this.data[iface][module][action];
  }
}

interface UsageReportPayload {
  usersFilter: string | null;
  dateFrom: Date | null;
  dateTo: Date | null;
  utcOffset: number;
}

interface EarliestTimestampResponse {
  earliestTimestamp: Date;
}

class InterfaceDto {
  interface: SystemUsageInterface;
  modules: ModuleDto[];
}

interface ModuleDto {
  module: SystemUsageModule;
  actions: ActionDto[];
}

interface ActionDto {
  action: SystemUsageAction;
  total: number;
}

class InterfaceModel {
  interface: SystemUsageInterface;
  modules: ModuleModel[];
  colspan: number;
  constructor(iface: InterfaceDto) {
    this.interface = iface.interface;
    this.modules = iface.modules.map(mod => new ModuleModel(mod));
    this.colspan = 0;
    this.modules.forEach((mod: ModuleModel) => {
      this.colspan += mod.colspan;
    });
  }
}

class ModuleModel {
  module: SystemUsageModule;
  actions: ActionDto[];
  colspan: number;
  constructor(mdto: ModuleDto) {
    this.module = mdto.module;
    this.actions = mdto.actions;
    this.colspan = this.actions.length;
  }
}